import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, API_BASE, type LoginResponse, type SessionUser } from '../lib/api';

const TOKEN_KEY = 'ctp_token';

/** Liest Token/Fehler aus dem SSO-Rücksprung-Fragment (#sso_access_token / #sso_error). */
function consumeSsoHash(): { token: string | null; error: string | null } {
  const raw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  if (!raw) return { token: null, error: null };
  const p = new URLSearchParams(raw);
  return { token: p.get('sso_access_token'), error: p.get('sso_error') };
}

interface AuthState {
  token: string | null;
  user: SessionUser | null;
  loading: boolean;
  ssoError: string | null;
  clearSsoError: () => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  startSso: () => void;
  /** Lädt das Profil (/me) erneut — z. B. nachdem der Nutzer seinen Namen ergänzt hat. */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  // WICHTIG: SSO-Token SYNCHRON beim ersten Render übernehmen, damit geschützte
  // Routen nicht vorher auf /login umleiten und das Fragment verwerfen.
  const [token, setToken] = useState<string | null>(() => consumeSsoHash().token ?? localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState<string | null>(() => consumeSsoHash().error);

  function persistToken(t: string | null): void {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
    setToken(t);
  }

  // SSO-Fragment einmalig „aufräumen": Token dauerhaft sichern, Hash aus der URL entfernen.
  useEffect(() => {
    const { token: ht, error } = consumeSsoHash();
    if (ht) localStorage.setItem(TOKEN_KEY, ht);
    if (ht || error) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // Profil bei Token-Änderung laden.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get<SessionUser>('/me', token)
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) {
          persistToken(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function login(email: string, password: string): Promise<void> {
    const res = await api.post<LoginResponse>('/auth/login', { email, password });
    setUser(res.user);
    persistToken(res.accessToken);
  }

  async function logout(): Promise<void> {
    if (token) await api.post('/auth/logout', {}, token).catch(() => undefined);
    setUser(null);
    persistToken(null);
  }

  function startSso(): void {
    setSsoError(null);
    window.location.href = `${API_BASE}/auth/microsoft/start`;
  }

  function clearSsoError(): void {
    setSsoError(null);
  }

  async function refreshUser(): Promise<void> {
    if (!token) return;
    try {
      setUser(await api.get<SessionUser>('/me', token));
    } catch {
      /* Profil-Refresh ist optional — der bestehende Stand bleibt erhalten. */
    }
  }

  return (
    <AuthContext.Provider
      value={{ token, user, loading, ssoError, clearSsoError, login, logout, startSso, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return ctx;
}
